import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { showGlobalToast } from "@/utils/showGlobalToast";

import { ListStatus, UpdateType } from "@/enum/list.enum";
import { useAppSelector } from "@/redux/hooks";
import { useUpdateListMutation } from "@/pages/Private/redux/list/list.api";
import { useGetActiveGenerationsQuery } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import {
	GenerationJob,
	selectGenerationJobs,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { useCompanyGenerationsSocket } from "@/pages/Private/pages/Feed/utils/useCompanyGenerationsSocket";
import { useFeedReviewNow } from "@/pages/Private/pages/Feed/utils/useFeedReviewNow";
import { AiQueuePill } from "@/pages/Private/pages/Feed/components/AiQueuePill";

import { reportActivationFailure } from "../utils/listRefusalMessage";

import { List, UpdateList } from "../schema/list";
import { CHAT_LIST_PERSON_PARAM } from "../utils/listDetailChatParam";
import { useListConfigurationStatus } from "../utils/useListConfigurationStatus";
import { describeListSetupRefusal, getListSetupRefusal } from "../utils/listSourceState";
import { ListEnrichmentToggle } from "./ListEnrichmentToggle";
import { ListNoFileHint } from "./ListNoFileHint";

/**
 * Level-1 nav status cluster for the Lead List detail (PREC-3641): mirrors the List Overview's
 * per-list status controls on the detail page's top bar, reusing the exact overview components so
 * look, states and hover-swaps stay identical —
 *   • {@link ListEnrichmentToggle} — List active / Deactivated (enrichment run-state toggle).
 *   • {@link AiQueuePill}         — "Generating N · M ready" AI drafts, scoped to this list;
 *     picking a lead opens that lead's chat in the table below (PREC-4103).
 *
 * Campaign status is no longer shown here: it lives in the header's Campaigns dropdown
 * (ListCampaignsDropdown, PREC-3703), which fetches its own summaries.
 *
 * Data wiring lives here (not in the overview table): the enrichment status mutation and the
 * company-wide generation jobs filtered down to this list.
 */
export const ListDetailStatusGroup: FunctionComponent<{ list: List }> = ({ list }) => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { consumeJob } = useFeedReviewNow();

	// Enrichment run-state toggle: flip ACTIVE ↔ PAUSED, mirroring the overview's handleToggle.
	const [update] = useUpdateListMutation();
	const handleToggle = useCallback(
		async (id: number, status: ListStatus) => {
			const updater: Partial<UpdateList> = { id, updateType: UpdateType.STATUS };

			if (status === ListStatus.ACTIVE) {
				updater.status = ListStatus.PAUSED;
			} else if (status === ListStatus.PAUSED) {
				updater.status = ListStatus.ACTIVE;
			}

			try {
				await update({ ...updater }).unwrap();
			} catch (error) {
				reportActivationFailure(error, t("list.status.stoppingError"), showGlobalToast);
			}
		},
		[update, t]
	);

	// Enrichment config status must come from the *full* list (with per-source arrays), not the
	// single lean list handed down here — the lean endpoint drops those arrays, which would make the
	// list read as unconfigured and wrongly disable the toggle (PREC-3621). This hook fetches the
	// full list and reads `currentFullList`; RerunListButton in the same header already triggers it.
	// `list` here is the *lean* list, which carries the lead counts the full list omits — without
	// them a hand-populated upload list reads as unconfigured (PREC-4118).
	const config = useListConfigurationStatus(list.id, list);
	// The shared setup decision names the step to finish (PREC-4537); every one of them blocks.
	const setupRefusal = getListSetupRefusal(config);
	const configBlocked = setupRefusal !== null;

	// Company-wide generation jobs, kept live via the shared query + socket, scoped to this list.
	useGetActiveGenerationsQuery();
	useCompanyGenerationsSocket();
	const allJobs = useAppSelector(selectGenerationJobs);
	const jobs = useMemo(() => allJobs.filter((job) => job.listId === list.id), [allJobs, list.id]);

	/**
	 * Picking a lead goes to *that lead*, here on this list: the table jumps to its tab, searches its
	 * name and opens its panel on the Chat view, where the drafts are.
	 *
	 * This used to `navigate` to the Feed and discard the `job` argument entirely (PREC-4103), so
	 * every row of the dropdown opened the tracker feed instead of the prospect's chat — and not even
	 * scoped to the list, because the Feed parses `listIds` as a JSON array and a bare number is
	 * dropped. The id goes through the URL rather than a callback because the table is a sibling
	 * component and the lead may be on another tab; see {@link CHAT_LIST_PERSON_PARAM}.
	 *
	 * `consumeJob` is the same review-marking the Feed host does, so the ready chip clears everywhere
	 * at once rather than only on the surface the user happened to use.
	 */
	const handleReview = useCallback(
		(job: GenerationJob) => {
			consumeJob(job);

			const nextParams = new URLSearchParams(searchParams);

			nextParams.set(CHAT_LIST_PERSON_PARAM, String(job.listPersonId));
			setSearchParams(nextParams, { replace: true });
		},
		[consumeJob, searchParams, setSearchParams]
	);

	return (
		<div className="flex shrink-0 items-center gap-2">
			<ListEnrichmentToggle
				configBlocked={configBlocked}
				disabledReason={setupRefusal ? describeListSetupRefusal(setupRefusal, t) : undefined}
				list={list}
				size="toolbar"
				onToggle={handleToggle}
			/>
			{config.sourceManualOnly && <ListNoFileHint sourceStepUrl={config.url} />}
			<AiQueuePill jobs={jobs} onSelectJob={handleReview} />
		</div>
	);
};
